import React, { useState, useEffect, useId, useRef } from "react";
import { db, storage } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { CloseCircle, Gallery, VideoSquare, Warning2 } from "iconsax-reactjs";
import { MAX_CAPTION_LENGTH } from "../../utils/reelsShared";
import Select from "../Select";
import "./reels.css";

const MAX_VIDEO_SIZE_MB = 150;
const MAX_VIDEO_DURATION_SEC = 90;

export default function CreateReelModal({ open, onClose, user, onPublished }) {
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);

  const [caption, setCaption] = useState("");
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");

  const [loadingProps, setLoadingProps] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const titleId = useId();
  const videoLabelId = useId();
  const videoHintId = useId();
  const listingId = useId();
  const captionId = useId();

  const videoInputRef = useRef(null);
  const coverInputRef = useRef(null);
  // The in-flight Storage upload, and whether the modal has gone away
  // (dealer switched tab mid-publish) so the remaining steps stop.
  const uploadTaskRef = useRef(null);
  const abortedRef = useRef(false);

  // Leaving the Reels tab unmounts the modal: cancel the upload instead of
  // letting up to 150MB keep going and publish a Reel nobody is watching.
  useEffect(() => {
    abortedRef.current = false;
    return () => {
      abortedRef.current = true;
      uploadTaskRef.current?.cancel();
    };
  }, []);

  // Closing or reloading the browser tab mid-upload can't be cancelled
  // cleanly, so warn before it happens.
  useEffect(() => {
    if (!uploading) return;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploading]);

  // Load dealer's properties once per open (not on every selection change).
  useEffect(() => {
    if (!open || !user?.uid) return;
    setLoadingProps(true);
    const q = query(
      collection(db, "properties"),
      where("ownerId", "==", user.uid)
    );
    getDocs(q)
      .then((snap) => {
        const list = [];
        snap.forEach((d) => {
          const data = d.data();
          if (data.status !== "deleted" && data.status !== "inactive") {
            list.push({ id: d.id, ...data });
          }
        });
        setProperties(list);
        setSelectedPropertyId((current) =>
          current && list.some((p) => p.id === current) ? current : (list[0]?.id || "")
        );
      })
      .catch((err) => console.error("Error loading properties:", err))
      .finally(() => setLoadingProps(false));
  }, [open, user?.uid]);

  // The modal stays mounted between opens, so clear the form on close;
  // otherwise the last video stays loaded and one click re-publishes it.
  useEffect(() => {
    if (open) return;
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoDuration(0);
    setCoverFile(null);
    setCoverPreviewUrl(null);
    setCaption("");
    setSelectedPropertyId("");
    setProgress(0);
    setError("");
  }, [open]);

  // Revoke each object URL only when it is replaced or on unmount. A single
  // effect over both URLs revoked the still-displayed video preview the
  // moment the auto-generated cover arrived.
  useEffect(() => () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
  }, [videoPreviewUrl]);
  useEffect(() => () => {
    if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
  }, [coverPreviewUrl]);

  if (!open) return null;

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
      setError(`Video file too large. Please select a video under ${MAX_VIDEO_SIZE_MB}MB.`);
      return;
    }

    setError("");
    setVideoFile(file);
    const url = URL.createObjectURL(file);
    setVideoPreviewUrl(url);

    // Auto extract first frame as cover
    const tempVideo = document.createElement("video");
    tempVideo.src = url;
    tempVideo.muted = true;
    tempVideo.playsInline = true;

    tempVideo.onloadedmetadata = () => {
      setVideoDuration(Math.round(tempVideo.duration || 0));
      if (tempVideo.duration > MAX_VIDEO_DURATION_SEC) {
        setError(`Reels must be under ${MAX_VIDEO_DURATION_SEC} seconds.`);
      }
      tempVideo.currentTime = Math.min(0.5, (tempVideo.duration || 1) / 2);
    };

    tempVideo.onseeked = () => {
      try {
        const canvas = document.createElement("canvas");
        const targetWidth = Math.min(720, tempVideo.videoWidth || 720);
        const aspect = (tempVideo.videoHeight || 1280) / (tempVideo.videoWidth || 720);
        canvas.width = targetWidth;
        canvas.height = Math.round(targetWidth * aspect);

        const ctx = canvas.getContext("2d");
        ctx.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              setCoverFile(blob);
              setCoverPreviewUrl(URL.createObjectURL(blob));
            }
          },
          "image/jpeg",
          0.85
        );
      } catch (err) {
        console.warn("Cover frame capture failed", err);
      }
    };
  };

  const handleCustomCoverSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreviewUrl(URL.createObjectURL(file));
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    if (!videoFile) {
      setError("Please select a video file.");
      return;
    }
    // Selecting an over-long video only showed a message; this is what
    // actually stops it being published.
    if (videoDuration > MAX_VIDEO_DURATION_SEC) {
      setError(`Reels must be under ${MAX_VIDEO_DURATION_SEC} seconds.`);
      return;
    }
    if (!coverFile) {
      setError("Please choose or generate a cover thumbnail.");
      return;
    }
    if (!selectedPropertyId) {
      setError("Please link an active listing.");
      return;
    }

    const selectedProperty = properties.find((p) => p.id === selectedPropertyId);
    if (!selectedProperty) {
      setError("Selected property not found.");
      return;
    }

    setUploading(true);
    setProgress(0);
    setError("");

    // Objects uploaded so far, removed again if a later step fails so a
    // failed publish doesn't leave up to 150MB orphaned in Storage.
    const uploadedRefs = [];

    try {
      const reelId = `reel_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 8)}`;
      const videoExt = (videoFile.name?.split(".").pop() || "mp4").toLowerCase();
      const videoPath = `reels/${user.uid}/${reelId}/video.${videoExt}`;
      const thumbPath = `reels/${user.uid}/${reelId}/cover.jpg`;

      // 1. Upload Video
      const videoRef = ref(storage, videoPath);
      const videoTask = uploadBytesResumable(videoRef, videoFile);
      uploadTaskRef.current = videoTask;

      await new Promise((resolve, reject) => {
        videoTask.on(
          "state_changed",
          (snap) => {
            if (snap.totalBytes > 0) {
              const ratio = snap.bytesTransferred / snap.totalBytes;
              setProgress(Math.round(ratio * 90));
            }
          },
          reject,
          resolve
        );
      });
      uploadedRefs.push(videoRef);
      if (abortedRef.current) throw new Error("Upload cancelled");
      const videoUrl = await getDownloadURL(videoRef);

      // 2. Upload Thumbnail Cover
      const thumbRef = ref(storage, thumbPath);
      const thumbTask = uploadBytesResumable(thumbRef, coverFile);
      uploadTaskRef.current = thumbTask;
      await thumbTask;
      uploadedRefs.push(thumbRef);
      const thumbnailUrl = await getDownloadURL(thumbRef);
      setProgress(100);
      // Don't publish a Reel the dealer walked away from mid-upload.
      if (abortedRef.current) throw new Error("Upload cancelled");

      // 3. Write Firestore Reel Doc
      const reelData = {
        propertyId: selectedProperty.id,
        dealerId: user.uid,
        videoUrl,
        thumbnailUrl,
        videoPath,
        thumbPath,
        caption: caption.trim(),
        city: selectedProperty.city || "",
        locality: selectedProperty.locality || "",
        geohash: selectedProperty.geohash || "",
        status: "active",
        sponsored: false,
        views: 0,
        saves: 0,
        shares: 0,
        enquiries: 0,
        commentsCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "propertyReels", reelId), reelData);

      if (onPublished) onPublished();
      onClose();
    } catch (err) {
      uploadedRefs.forEach((r) => deleteObject(r).catch(() => {}));
      if (!abortedRef.current) {
        console.error("Reel publishing failed:", err);
        setError(err.message || "Failed to publish Reel. Please try again.");
      }
    } finally {
      uploadTaskRef.current = null;
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !uploading && onClose()}>
      <div
        className="modal reel-create-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-header">
          <div className="reel-modal-heading">
            <h2 id={titleId}>Upload reel</h2>
            <p className="reel-modal-sub">Publish a short video linked to one of your listings.</p>
          </div>
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            disabled={uploading}
            aria-label="Close"
          >
            <CloseCircle size={20} variant="Linear" />
          </button>
        </div>

        <form onSubmit={handlePublish}>
          <div className="modal-body">
            {error && (
              <p className="reel-error-banner" role="alert">
                <Warning2 size={18} variant="Linear" aria-hidden="true" />
                {error}
              </p>
            )}

            {/* Video file picker */}
            <div className="field">
              <span className="field-label" id={videoLabelId}>Video</span>
              <input
                type="file"
                ref={videoInputRef}
                accept="video/mp4,video/quicktime,video/webm"
                onChange={handleVideoSelect}
                className="reel-file-input"
                aria-labelledby={videoLabelId}
              />
              {videoPreviewUrl ? (
                <div className="reel-create-video">
                  <div className="reel-stage">
                    <video src={videoPreviewUrl} controls aria-labelledby={videoLabelId} />
                  </div>
                  <div className="reel-create-video-bar">
                    <span className="field-hint">
                      {videoDuration > 0 ? `${videoDuration} seconds` : "Reading video length"}
                    </span>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => videoInputRef.current?.click()}
                      disabled={uploading}
                    >
                      Change video
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="reel-dropzone"
                  onClick={() => videoInputRef.current?.click()}
                  aria-describedby={videoHintId}
                >
                  <span className="reel-dropzone-icon" aria-hidden="true">
                    <VideoSquare size={32} variant="Linear" />
                  </span>
                  <span className="reel-dropzone-title">Choose a video</span>
                  <span className="field-hint" id={videoHintId}>
                    MP4, MOV or WebM under {MAX_VIDEO_DURATION_SEC} seconds and {MAX_VIDEO_SIZE_MB} MB. Portrait 9:16 works best.
                  </span>
                </button>
              )}
            </div>

            {/* Cover thumbnail */}
            {videoPreviewUrl && (
              <div className="field">
                <span className="field-label">Cover</span>
                <div className="reel-create-cover">
                  <div className="reel-create-cover-frame">
                    {coverPreviewUrl ? (
                      <img src={coverPreviewUrl} alt="Cover preview" />
                    ) : (
                      <Gallery size={22} variant="Linear" aria-hidden="true" />
                    )}
                  </div>
                  <div className="reel-create-cover-text">
                    <span className="field-hint">
                      A frame from the video is used by default. Upload your own image to replace it.
                    </span>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploading}
                    >
                      Upload custom cover
                    </button>
                  </div>
                  <input
                    type="file"
                    ref={coverInputRef}
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleCustomCoverSelect}
                    className="reel-file-input"
                    aria-label="Custom cover image"
                  />
                </div>
              </div>
            )}

            {/* Link to Property */}
            <div className="field">
              <label className="field-label" htmlFor={listingId}>Listing</label>
              {loadingProps ? (
                <div className="skeleton reel-create-select-skeleton" role="status">
                  <span className="sr-only">Loading your listings</span>
                </div>
              ) : properties.length === 0 ? (
                <span className="field-error">You need at least one active listing to publish a reel.</span>
              ) : (
                <Select
                  id={listingId}
                  value={selectedPropertyId}
                  onChange={setSelectedPropertyId}
                  placeholder="Choose a listing"
                  searchPlaceholder="Search your listings"
                  required
                  options={properties.map((p) => ({
                    value: p.id,
                    label: p.title || "Untitled listing",
                    detail: [
                      p.price != null && `₹${Number(p.price).toLocaleString("en-IN")}`,
                      p.locality || p.city,
                    ].filter(Boolean).join(" · "),
                  }))}
                />
              )}
            </div>

            {/* Caption */}
            <div className="field">
              <div className="reel-field-row">
                <label className="field-label" htmlFor={captionId}>Caption</label>
                <span className="reel-char-count">
                  {caption.length}/{MAX_CAPTION_LENGTH}
                </span>
              </div>
              <textarea
                id={captionId}
                className="textarea"
                rows="3"
                value={caption}
                maxLength={MAX_CAPTION_LENGTH}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Key features, rent or price, and when it is available"
              />
            </div>

            {/* Upload progress */}
            {uploading && (
              <div className="reel-progress">
                <div className="reel-progress-label">
                  <span>Uploading reel</span>
                  <span>{progress}%</span>
                </div>
                <div
                  className="reel-progress-track"
                  role="progressbar"
                  aria-label="Upload progress"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                >
                  <div className="reel-progress-fill" style={{ transform: `scaleX(${progress / 100})` }} />
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={uploading || !videoFile || !selectedPropertyId}
            >
              {uploading && <span className="btn-spinner" aria-hidden="true" />}
              {uploading ? `Publishing (${progress}%)` : "Publish reel"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
